import { useFocusFinders } from '@fluentui/react-components';
import { getKey, KeyboardKey } from '@office-start/keystroke-handler-utility';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useItemRefs } from './useItemRefs';
export var useKeyboardNavigation = function (_a) {
    var arrowKeyListNavEnabled = _a.arrowKeyListNavEnabled, cardsPerPage = _a.cardsPerPage, firstItemIndex = _a.firstItemIndex, focusedIndex = _a.focusedIndex, isLastPage = _a.isLastPage, isNavigatingByKeyboardRef = _a.isNavigatingByKeyboardRef, isRtl = _a.isRtl, navNext = _a.navNext, navPrevious = _a.navPrevious, numberOfItems = _a.numberOfItems;
    var containerRef = useRef(null);
    var focusFinders = useFocusFinders();
    var itemRefs = useItemRefs(arrowKeyListNavEnabled, numberOfItems);
    /**
     * Moves focus to previous card on arrow left
     */
    var focusDecrement = useCallback(function (e) {
        var _a, _b;
        if (focusedIndex.current === null) {
            return;
        }
        var nextIndex = focusedIndex.current - 1;
        if (nextIndex < 0) {
            return;
        }
        focusedIndex.current = nextIndex;
        if (nextIndex < firstItemIndex) {
            e.stopPropagation();
            e.preventDefault();
            navPrevious(nextIndex);
        }
        else {
            (_b = (_a = itemRefs[nextIndex]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [firstItemIndex, focusedIndex, itemRefs, navPrevious]);
    /**
     * Moves focus to next card on arrow right
     */
    var focusIncrement = useCallback(function (e) {
        var _a, _b;
        if (focusedIndex.current === null) {
            return;
        }
        var nextIndex = focusedIndex.current + 1;
        // Handle edge case where keyboard navs to last page, but 'navNext' is not triggered
        // If 'navNext' is called here focus is lost since a new page is not being navigated to
        // and items are not being re-rendered from the list. Focus is being set, but items do not appear to be focused.
        if (nextIndex >= numberOfItems) {
            if (!isLastPage) {
                e.stopPropagation();
                e.preventDefault();
                navNext(focusedIndex.current);
            }
            return;
        }
        focusedIndex.current = nextIndex;
        if (nextIndex - firstItemIndex > cardsPerPage) {
            e.stopPropagation();
            e.preventDefault();
            navNext(nextIndex);
        }
        else {
            (_b = (_a = itemRefs[nextIndex]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [
        cardsPerPage,
        firstItemIndex,
        focusedIndex,
        isLastPage,
        itemRefs,
        navNext,
        numberOfItems,
    ]);
    var onBlur = useCallback(function (_e) {
        isNavigatingByKeyboardRef.current = false;
    }, [isNavigatingByKeyboardRef]);
    /**
     * When tab is pressed, navigate to the previos (shifKey) or next focusable element
     */
    var focusNextOrPrevious = useCallback(function (e) {
        if (!containerRef.current) {
            return;
        }
        var toBeFocused = e.shiftKey
            ? focusFinders.findPrevFocusable(containerRef.current)
            : focusFinders.findNextFocusable(containerRef.current);
        if (!toBeFocused) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        toBeFocused.focus();
    }, [focusFinders]);
    /**
     * When a key is pressed on the container, proxy the focus to the appropriate element
     */
    var handleContainerKeyDown = useCallback(function (e) {
        var _a;
        var keyHandlerMap = (_a = {},
            _a[KeyboardKey.ArrowLeft] = isRtl
                ? focusIncrement
                : focusDecrement,
            _a[KeyboardKey.ArrowRight] = isRtl
                ? focusDecrement
                : focusIncrement,
            _a[KeyboardKey.Tab] = focusNextOrPrevious,
            _a);
        var key = getKey(e);
        if (!keyHandlerMap[key]) {
            return;
        }
        keyHandlerMap[key](e);
    }, [focusDecrement, focusIncrement, focusNextOrPrevious, isRtl]);
    /**
     * Sets the initial focus on the appropriate index inside the container
     */
    var onFocusCapture = useCallback(function (e) {
        var _a, _b;
        // If children elements indicate attribute data-skip-capture={true} skip focus on itemRef.
        if ((_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.getAttribute('data-skip-capture'))
            return;
        if (!focusedIndex.current) {
            focusedIndex.current = firstItemIndex;
        }
        var toBeFocused = itemRefs[focusedIndex.current].current;
        if (toBeFocused) {
            toBeFocused.focus();
        }
        else {
            (_b = itemRefs[firstItemIndex].current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [firstItemIndex, focusedIndex, itemRefs]);
    // When pages are changed outside of this hook (via nav) set focus
    useEffect(function () {
        if (arrowKeyListNavEnabled &&
            isNavigatingByKeyboardRef.current &&
            focusedIndex.current !== null) {
            onFocusCapture();
            isNavigatingByKeyboardRef.current = false;
        }
    }, [
        arrowKeyListNavEnabled,
        focusedIndex,
        isNavigatingByKeyboardRef,
        onFocusCapture,
    ]);
    return useMemo(function () {
        if (!arrowKeyListNavEnabled) {
            return undefined;
        }
        return {
            containerRef: containerRef,
            handleContainerKeyDown: handleContainerKeyDown,
            itemRefs: itemRefs,
            onBlur: onBlur,
            onFocusCapture: onFocusCapture,
        };
    }, [
        arrowKeyListNavEnabled,
        handleContainerKeyDown,
        itemRefs,
        onBlur,
        onFocusCapture,
    ]);
};
//# sourceMappingURL=useKeyboardNavigation.js.map